import React, { useEffect, useState } from 'react';
import { apiClient } from 'Utils/api';
import { useTranslation } from 'react-i18next';
import './FilterSidebar.css';

interface FilterSidebarProps {
  filters: any;
  onChange: (filters: any) => void;
  onApply: (filters: any) => void;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ filters, onChange, onApply }) => {
  const { t } = useTranslation();
  const [filterOptions, setFilterOptions] = useState<any>(null);
  const [localFilters, setLocalFilters] = useState(filters);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Sync local sidebar state when parent filters change (e.g. URL params updated)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => {
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      // Pass current filters to get faceted results
      const params: any = {};
      if (filters.price_min) params.price_min = filters.price_min;
      if (filters.price_max) params.price_max = filters.price_max;
      if (filters.brands?.length > 0) params.brands = filters.brands;

      const response = await apiClient.get(`/api/watches/filter-options`, { params });
      setFilterOptions(response.data.filters);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleBrandToggle = (brand: string) => {
    const newBrands = localFilters.brands.includes(brand)
      ? localFilters.brands.filter((b: string) => b !== brand)
      : [...localFilters.brands, brand];

    setLocalFilters({ ...localFilters, brands: newBrands });
  };

  const handleCategoryOptionToggle = (categoryId: string, optionId: string) => {
    const categoryOptions = localFilters.categories[categoryId] || [];
    const newOptions = categoryOptions.includes(optionId)
      ? categoryOptions.filter((id: string) => id !== optionId)
      : [...categoryOptions, optionId];

    const newCategories = { ...localFilters.categories };
    if (newOptions.length === 0) {
      delete newCategories[categoryId];
    } else {
      newCategories[categoryId] = newOptions;
    }

    setLocalFilters({ ...localFilters, categories: newCategories });
  };

  const handlePriceChange = (type: 'min' | 'max', value: number) => {
    const newFilters = { ...localFilters };
    if (type === 'min') {
      newFilters.price_min = value;
    } else {
      newFilters.price_max = value;
    }
    setLocalFilters(newFilters);
  };

  const handleAvailableOnlyToggle = (checked: boolean) => {
    const newFilters = { ...localFilters };
    if (checked) {
      newFilters.status = 'AVAILABLE';
    } else {
      delete newFilters.status;
    }
    setLocalFilters(newFilters);
  };

  const handleReferenceChange = (value: string) => {
    setLocalFilters({ ...localFilters, referenceNumber: value });
  };

  const handleReferenceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  const handleApply = () => {
    onChange(localFilters);
    onApply(localFilters);
    setIsDrawerOpen(false);
  };

  const handleReset = () => {
    const resetFilters = { brands: [], categories: {} };
    setLocalFilters(resetFilters);
    onChange(resetFilters);
    onApply(resetFilters);
  };

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  if (!filterOptions) return <div className="filter-sidebar">{t('shop.loading_short')}</div>;

  const minPrice = filterOptions.priceRange?.min || 0;
  const maxPrice = filterOptions.priceRange?.max || 100000;
  const currentMin = localFilters.price_min || minPrice;
  const currentMax = localFilters.price_max || maxPrice;

  return (
    <>
      <button className="filter-toggle-btn" onClick={toggleDrawer}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 4h16M2 10h10M2 16h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        {t('filter.filter_btn')}
      </button>

      {isDrawerOpen && <div className="filter-overlay" onClick={toggleDrawer}></div>}

      <aside className={`filter-sidebar ${isDrawerOpen ? 'drawer-open' : ''}`}>
        <div className="filter-header">
          <h2>{t('filter.title')}</h2>
          <button className="reset-button" onClick={handleReset}>{t('filter.reset')}</button>
        </div>

        <div className="filter-section">
          <h3>{t('filter.reference')}</h3>
          <div className="reference-search">
            <input
              type="text"
              className="reference-search-input"
              placeholder={t('filter.reference_placeholder')}
              value={localFilters.referenceNumber || ''}
              onChange={(e) => handleReferenceChange(e.target.value)}
              onKeyDown={handleReferenceKeyDown}
              data-testid="filter-reference-input"
            />
          </div>
        </div>

        <div className="filter-section">
          <h3>{t('filter.availability')}</h3>
          <div className="filter-options">
            <label className="filter-checkbox" data-testid="filter-available-only">
              <input
                type="checkbox"
                checked={localFilters.status === 'AVAILABLE'}
                onChange={(e) => handleAvailableOnlyToggle(e.target.checked)}
              />
              <span>{t('filter.available_only')}</span>
            </label>
          </div>
        </div>

        <div className="filter-section">
          <h3>{t('filter.price')}</h3>
          <div className="price-range-container">
            <div className="dual-range-slider">
              <input
                type="range"
                className="price-slider price-slider-min"
                min={minPrice}
                max={maxPrice}
                value={currentMin}
                onChange={(e) => {
                  const value = Math.min(parseFloat(e.target.value), currentMax - 100);
                  handlePriceChange('min', value);
                }}
              />
              <input
                type="range"
                className="price-slider price-slider-max"
                min={minPrice}
                max={maxPrice}
                value={currentMax}
                onChange={(e) => {
                  const value = Math.max(parseFloat(e.target.value), currentMin + 100);
                  handlePriceChange('max', value);
                }}
              />
            </div>
            <div className="price-values">
              <span>{t('filter.from')} <span className="price-value">{currentMin.toLocaleString('de-DE')} €</span></span>
              <span>{t('filter.to')} <span className="price-value">{currentMax.toLocaleString('de-DE')} €</span></span>
            </div>
          </div>
        </div>

        {filterOptions?.brands && filterOptions.brands.length > 0 && (
          <div className="filter-section">
            <h3>{t('filter.brand')}</h3>
            <div className="filter-options">
              {filterOptions.brands.map((brand: string) => (
                <label key={brand} className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={localFilters.brands.includes(brand)}
                    onChange={() => handleBrandToggle(brand)}
                  />
                  <span>{brand}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {filterOptions?.categories && filterOptions.categories.map((category: any) => (
          <div key={category.id} className="filter-section">
            <h3>{category.name}</h3>
            <div className="filter-options">
              {category.options.map((option: any) => (
                <label key={option.id} className="filter-checkbox">
                  <input
                    type="checkbox"
                    checked={(localFilters.categories[category.id] || []).includes(option.id)}
                    onChange={() => handleCategoryOptionToggle(category.id, option.id)}
                  />
                  <span>{option.value}</span>
                </label>
              ))}
            </div>
          </div>
        ))}

        <div className="apply-button-container">
          <button className="apply-button" onClick={handleApply}>
            {t('filter.apply')}
          </button>
        </div>
      </aside>
    </>
  );
};

export default FilterSidebar;
